import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useColorMode } from '../hooks/use-color-mode';
import { CONSENT_CATEGORIES, diffCategories, eraseCookies, hasValidConsent, isConsentValid, NECESSARY_CATEGORY, readAllowedCategories, readConsentCookie, resolveAcceptedCategories, writeConsent } from '../utils/consent-cookie';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
export const COOKIE_CONSENT_CHANGE_EVENT = 'jaen:cookie-consent-change';
const FALLBACK_LANGUAGE = 'en';
const analyticsCookieTable = (settings, labels) => {
    if (!settings?.useGoogleAnalytics)
        return [];
    return [
        {
            col1: '^_ga',
            col2: 'google.com',
            col3: labels.twoYears,
            col4: labels.description,
            is_regex: true
        },
        {
            col1: '_gid',
            col2: 'google.com',
            col3: labels.oneDay,
            col4: labels.description
        }
    ];
};
const buildLanguages = (settings) => ({
    en: {
        consent_modal: {
            title: 'We use cookies!',
            description: 'Hi, this website uses essential cookies to ensure its proper operation and tracking cookies to understand how you interact with it. The latter will be set only after consent. <button type="button" data-cc="c-settings" class="cc-link">Let me choose</button>',
            primary_btn: {
                text: 'Accept all',
                role: 'accept_all'
            },
            secondary_btn: {
                text: 'Reject all',
                role: 'accept_necessary'
            }
        },
        settings_modal: {
            title: 'Cookie Settings',
            save_settings_btn: 'Save settings',
            accept_all_btn: 'Accept all',
            reject_all_btn: 'Reject all',
            close_btn_label: 'Close',
            cookie_table_headers: [
                { col1: 'Name' },
                { col2: 'Domain' },
                { col3: 'Expiration' },
                { col4: 'Description' }
            ],
            blocks: [
                {
                    title: 'Cookie usage 📢',
                    description: 'I use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want. For more details relative to cookies and other sensitive data, please read the full <a href="#" class="cc-link">privacy policy</a>.'
                },
                {
                    title: 'Strictly necessary cookies',
                    description: 'These cookies are essential for the proper functioning of my website. Without these cookies, the website would not work properly',
                    toggle: {
                        value: 'necessary',
                        enabled: true,
                        readonly: true
                    }
                },
                {
                    title: 'Performance and Analytics cookies',
                    description: 'These cookies allow the website to remember the choices you have made in the past',
                    toggle: {
                        value: 'analytics',
                        enabled: false,
                        readonly: false
                    },
                    cookie_table: analyticsCookieTable(settings, {
                        twoYears: '2 years',
                        oneDay: '1 day',
                        description: 'Used to distinguish users in Google Analytics. This cookie helps us understand how visitors interact with our website.'
                    })
                },
                {
                    title: 'Advertisement and Targeting cookies',
                    description: 'These cookies collect information about how you use the website, which pages you visited and which links you clicked on. All of the data is anonymized and cannot be used to identify you',
                    toggle: {
                        value: 'targeting',
                        enabled: false,
                        readonly: false
                    }
                },
                {
                    title: 'More information',
                    description: 'For any queries in relation to our policy on cookies and your choices, please <a class="cc-link" href="/contakt">contact us</a>.'
                }
            ]
        }
    },
    de: {
        consent_modal: {
            title: 'Wir verwenden Cookies!',
            description: 'Hallo, diese Website verwendet notwendige Cookies für ihren ordnungsgemäßen Betrieb und Tracking-Cookies, um zu verstehen, wie Sie mit ihr umgehen. Letztere werden erst nach Ihrer Zustimmung gesetzt. <button type="button" data-cc="c-settings" class="cc-link">Selbst auswählen</button>',
            primary_btn: {
                text: 'Alle akzeptieren',
                role: 'accept_all'
            },
            secondary_btn: {
                text: 'Alle ablehnen',
                role: 'accept_necessary'
            }
        },
        settings_modal: {
            title: 'Cookie-Einstellungen',
            save_settings_btn: 'Einstellungen speichern',
            accept_all_btn: 'Alle akzeptieren',
            reject_all_btn: 'Alle ablehnen',
            close_btn_label: 'Schließen',
            cookie_table_headers: [
                { col1: 'Name' },
                { col2: 'Domain' },
                { col3: 'Ablauf' },
                { col4: 'Beschreibung' }
            ],
            blocks: [
                {
                    title: 'Verwendung von Cookies 📢',
                    description: 'Wir verwenden Cookies, um die grundlegenden Funktionen der Website sicherzustellen und Ihr Online-Erlebnis zu verbessern. Für jede Kategorie können Sie sich jederzeit dafür oder dagegen entscheiden. Weitere Einzelheiten zu Cookies und anderen sensiblen Daten finden Sie in der vollständigen <a href="#" class="cc-link">Datenschutzerklärung</a>.'
                },
                {
                    title: 'Unbedingt erforderliche Cookies',
                    description: 'Diese Cookies sind für den ordnungsgemäßen Betrieb unserer Website unerlässlich. Ohne diese Cookies würde die Website nicht richtig funktionieren',
                    toggle: {
                        value: 'necessary',
                        enabled: true,
                        readonly: true
                    }
                },
                {
                    title: 'Cookies für Leistung und Analyse',
                    description: 'Diese Cookies ermöglichen es der Website, sich an Ihre früheren Entscheidungen zu erinnern',
                    toggle: {
                        value: 'analytics',
                        enabled: false,
                        readonly: false
                    },
                    cookie_table: analyticsCookieTable(settings, {
                        twoYears: '2 Jahre',
                        oneDay: '1 Tag',
                        description: 'Dient dazu, Nutzer in Google Analytics zu unterscheiden. Dieses Cookie hilft uns zu verstehen, wie Besucher mit unserer Website umgehen.'
                    })
                },
                {
                    title: 'Cookies für Werbung und Targeting',
                    description: 'Diese Cookies erfassen Informationen darüber, wie Sie die Website nutzen, welche Seiten Sie besucht und welche Links Sie angeklickt haben. Alle Daten werden anonymisiert und können nicht zu Ihrer Identifizierung verwendet werden',
                    toggle: {
                        value: 'targeting',
                        enabled: false,
                        readonly: false
                    }
                },
                {
                    title: 'Weitere Informationen',
                    description: 'Bei Fragen zu unserer Cookie-Richtlinie und zu Ihren Auswahlmöglichkeiten <a class="cc-link" href="/contakt">kontaktieren Sie uns bitte</a>.'
                }
            ]
        }
    },
    sl: {
        consent_modal: {
            title: 'Uporabljamo piškotke!',
            description: 'Pozdravljeni, to spletno mesto uporablja nujne piškotke za pravilno delovanje in piškotke za sledenje, da razumemo, kako ga uporabljate. Slednji se nastavijo šele po vaši privolitvi. <button type="button" data-cc="c-settings" class="cc-link">Želim izbrati sam</button>',
            primary_btn: {
                text: 'Sprejmi vse',
                role: 'accept_all'
            },
            secondary_btn: {
                text: 'Zavrni vse',
                role: 'accept_necessary'
            }
        },
        settings_modal: {
            title: 'Nastavitve piškotkov',
            save_settings_btn: 'Shrani nastavitve',
            accept_all_btn: 'Sprejmi vse',
            reject_all_btn: 'Zavrni vse',
            close_btn_label: 'Zapri',
            cookie_table_headers: [
                { col1: 'Ime' },
                { col2: 'Domena' },
                { col3: 'Veljavnost' },
                { col4: 'Opis' }
            ],
            blocks: [
                {
                    title: 'Uporaba piškotkov 📢',
                    description: 'Piškotke uporabljamo za zagotavljanje osnovnih funkcij spletnega mesta in za izboljšanje vaše spletne izkušnje. Za vsako kategorijo se lahko kadar koli odločite za privolitev ali zavrnitev. Več podrobnosti o piškotkih in drugih občutljivih podatkih najdete v celotni <a href="#" class="cc-link">izjavi o varstvu podatkov</a>.'
                },
                {
                    title: 'Nujno potrebni piškotki',
                    description: 'Ti piškotki so nujni za pravilno delovanje našega spletnega mesta. Brez teh piškotkov spletno mesto ne bi delovalo pravilno',
                    toggle: {
                        value: 'necessary',
                        enabled: true,
                        readonly: true
                    }
                },
                {
                    title: 'Piškotki za zmogljivost in analitiko',
                    description: 'Ti piškotki spletnemu mestu omogočajo, da si zapomni vaše pretekle izbire',
                    toggle: {
                        value: 'analytics',
                        enabled: false,
                        readonly: false
                    },
                    cookie_table: analyticsCookieTable(settings, {
                        twoYears: '2 leti',
                        oneDay: '1 dan',
                        description: 'Uporablja se za razlikovanje uporabnikov v storitvi Google Analytics. Ta piškotek nam pomaga razumeti, kako obiskovalci uporabljajo naše spletno mesto.'
                    })
                },
                {
                    title: 'Piškotki za oglaševanje in ciljanje',
                    description: 'Ti piškotki zbirajo informacije o tem, kako uporabljate spletno mesto, katere strani ste obiskali in na katere povezave ste kliknili. Vsi podatki so anonimizirani in vas z njimi ni mogoče identificirati',
                    toggle: {
                        value: 'targeting',
                        enabled: false,
                        readonly: false
                    }
                },
                {
                    title: 'Več informacij',
                    description: 'Za vsa vprašanja o naši politiki piškotkov in o vaših izbirah nas <a class="cc-link" href="/contakt">kontaktirajte</a>.'
                }
            ]
        }
    },
    it: {
        consent_modal: {
            title: 'Utilizziamo i cookie!',
            description: 'Ciao, questo sito web utilizza cookie essenziali per il suo corretto funzionamento e cookie di tracciamento per capire come interagite con esso. Questi ultimi vengono impostati solo dopo il vostro consenso. <button type="button" data-cc="c-settings" class="cc-link">Scelgo io</button>',
            primary_btn: {
                text: 'Accetta tutti',
                role: 'accept_all'
            },
            secondary_btn: {
                text: 'Rifiuta tutti',
                role: 'accept_necessary'
            }
        },
        settings_modal: {
            title: 'Impostazioni dei cookie',
            save_settings_btn: 'Salva le impostazioni',
            accept_all_btn: 'Accetta tutti',
            reject_all_btn: 'Rifiuta tutti',
            close_btn_label: 'Chiudi',
            cookie_table_headers: [
                { col1: 'Nome' },
                { col2: 'Dominio' },
                { col3: 'Scadenza' },
                { col4: 'Descrizione' }
            ],
            blocks: [
                {
                    title: 'Utilizzo dei cookie 📢',
                    description: 'Utilizziamo i cookie per garantire le funzionalità di base del sito web e per migliorare la vostra esperienza online. Per ogni categoria potete scegliere in qualsiasi momento se attivarla o disattivarla. Per maggiori dettagli sui cookie e sugli altri dati sensibili consultate l\'<a href="#" class="cc-link">informativa sulla privacy</a> completa.'
                },
                {
                    title: 'Cookie strettamente necessari',
                    description: 'Questi cookie sono essenziali per il corretto funzionamento del nostro sito web. Senza questi cookie il sito non funzionerebbe correttamente',
                    toggle: {
                        value: 'necessary',
                        enabled: true,
                        readonly: true
                    }
                },
                {
                    title: 'Cookie di prestazione e analisi',
                    description: 'Questi cookie consentono al sito web di ricordare le scelte che avete fatto in passato',
                    toggle: {
                        value: 'analytics',
                        enabled: false,
                        readonly: false
                    },
                    cookie_table: analyticsCookieTable(settings, {
                        twoYears: '2 anni',
                        oneDay: '1 giorno',
                        description: 'Utilizzato per distinguere gli utenti in Google Analytics. Questo cookie ci aiuta a capire come i visitatori interagiscono con il nostro sito web.'
                    })
                },
                {
                    title: 'Cookie di pubblicità e targeting',
                    description: 'Questi cookie raccolgono informazioni su come utilizzate il sito web, quali pagine avete visitato e su quali link avete fatto clic. Tutti i dati sono anonimizzati e non possono essere utilizzati per identificarvi',
                    toggle: {
                        value: 'targeting',
                        enabled: false,
                        readonly: false
                    }
                },
                {
                    title: 'Maggiori informazioni',
                    description: 'Per qualsiasi domanda sulla nostra politica dei cookie e sulle vostre scelte <a class="cc-link" href="/contakt">contattateci</a>.'
                }
            ]
        }
    },
    ja: {
        consent_modal: {
            title: 'Cookie を使用しています',
            description: '当ウェブサイトでは、正常な動作に必要な Cookie と、ご利用状況を把握するためのトラッキング Cookie を使用しています。後者はお客様の同意をいただいた後にのみ設定されます。<button type="button" data-cc="c-settings" class="cc-link">個別に選択する</button>',
            primary_btn: {
                text: 'すべて許可',
                role: 'accept_all'
            },
            secondary_btn: {
                text: 'すべて拒否',
                role: 'accept_necessary'
            }
        },
        settings_modal: {
            title: 'Cookie 設定',
            save_settings_btn: '設定を保存',
            accept_all_btn: 'すべて許可',
            reject_all_btn: 'すべて拒否',
            close_btn_label: '閉じる',
            cookie_table_headers: [
                { col1: '名称' },
                { col2: 'ドメイン' },
                { col3: '有効期限' },
                { col4: '説明' }
            ],
            blocks: [
                {
                    title: 'Cookie の使用について 📢',
                    description: 'ウェブサイトの基本的な機能を確保し、オンライン体験を向上させるために Cookie を使用しています。カテゴリごとに、いつでも有効または無効をお選びいただけます。Cookie およびその他の機微な情報の詳細については、<a href="#" class="cc-link">プライバシーポリシー</a>の全文をご覧ください。'
                },
                {
                    title: '必須 Cookie',
                    description: 'これらの Cookie は当ウェブサイトが正しく動作するために不可欠です。これらの Cookie がない場合、ウェブサイトは正常に機能しません',
                    toggle: {
                        value: 'necessary',
                        enabled: true,
                        readonly: true
                    }
                },
                {
                    title: 'パフォーマンスおよび分析 Cookie',
                    description: 'これらの Cookie により、ウェブサイトはお客様がこれまでに行った選択を記憶できます',
                    toggle: {
                        value: 'analytics',
                        enabled: false,
                        readonly: false
                    },
                    cookie_table: analyticsCookieTable(settings, {
                        twoYears: '2 年',
                        oneDay: '1 日',
                        description: 'Google アナリティクスでユーザーを識別するために使用されます。この Cookie は、訪問者が当ウェブサイトをどのように利用しているかを把握するために役立ちます。'
                    })
                },
                {
                    title: '広告およびターゲティング Cookie',
                    description: 'これらの Cookie は、ウェブサイトの利用方法、閲覧したページ、クリックしたリンクに関する情報を収集します。すべてのデータは匿名化されており、お客様個人を特定することはできません',
                    toggle: {
                        value: 'targeting',
                        enabled: false,
                        readonly: false
                    }
                },
                {
                    title: '詳細情報',
                    description: 'Cookie に関する当社の方針やお客様の選択についてご不明な点がございましたら、<a class="cc-link" href="/contakt">お問い合わせください</a>。'
                }
            ]
        }
    }
});
const buildPluginConfig = (settings) => {
    const pluginConfig = {
        current_lang: FALLBACK_LANGUAGE,
        autoclear_cookies: true,
        page_scripts: true,
        gui_options: {
            settings_modal: {
                layout: 'box'
            },
            consent_modal: {
                layout: 'cloud'
            }
        },
        languages: buildLanguages(settings)
    };
    return pluginConfig;
};
const resolveLanguage = (locale, languages) => {
    const [base = ''] = (locale ?? '').replace(/_/g, '-').toLowerCase().split('-');
    return Object.prototype.hasOwnProperty.call(languages, base)
        ? base
        : FALLBACK_LANGUAGE;
};
const dispatchConsentChange = (detail) => {
    if (typeof window === 'undefined')
        return;
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGE_EVENT, {
        detail
    }));
};
const STATIC_BANNER_ATTRIBUTE = 'data-jaen-cc-banner';
const BANNER_VISIBILITY_EVENT = 'jaen:cookie-consent-banner-visibility';
const setBannerVisible = (visible) => {
    if (typeof window === 'undefined')
        return;
    window.dispatchEvent(new CustomEvent(BANNER_VISIBILITY_EVENT, { detail: visible }));
};
let pluginInstance = null;
let pluginPromise = null;
const pluginSettings = {};
const loadCookieConsentPlugin = async () => {
    if (typeof window === 'undefined')
        return null;
    if (pluginInstance)
        return pluginInstance;
    if (pluginPromise)
        return await pluginPromise;
    pluginPromise = (async () => {
        await import('vanilla-cookieconsent');
        if (typeof window.initCookieConsent !== 'function')
            return null;
        const cc = window.initCookieConsent();
        const pluginConfig = buildPluginConfig({
            useGoogleAnalytics: pluginSettings.useGoogleAnalytics
        });
        cc.run({
            ...pluginConfig,
            current_lang: resolveLanguage(pluginSettings.locale, pluginConfig.languages ?? {}),
            autorun: false,
            onAccept: cookie => {
                dispatchConsentChange({
                    categories: cookie?.categories ?? [],
                    changedCategories: []
                });
            },
            onChange: (cookie, changedCookieCategories) => {
                dispatchConsentChange({
                    categories: cookie?.categories ?? [],
                    changedCategories: changedCookieCategories ?? []
                });
            }
        });
        const orphans = document.querySelectorAll(`#cc--main:not([${STATIC_BANNER_ATTRIBUTE}]) #cm,` +
            `#cc--main:not([${STATIC_BANNER_ATTRIBUTE}]) #cm-ov`);
        orphans.forEach(node => {
            node.remove();
        });
        pluginInstance = cc;
        return cc;
    })().catch(() => {
        pluginPromise = null;
        return null;
    });
    return await pluginPromise;
};
const withPlugin = (use) => {
    if (pluginInstance) {
        use(pluginInstance);
        return;
    }
    void loadCookieConsentPlugin().then(plugin => {
        if (plugin)
            use(plugin);
    });
};
const consentApi = {
    run: config => {
        withPlugin(plugin => {
            plugin.run(config);
        });
    },
    showSettings: delay => {
        withPlugin(plugin => {
            plugin.showSettings(delay);
        });
    },
    hideSettings: () => {
        pluginInstance?.hideSettings();
    },
    hide: () => {
        setBannerVisible(false);
        pluginInstance?.hide();
    },
    show: (delay, createModal) => {
        setBannerVisible(true);
        pluginInstance?.show(delay, createModal);
    },
    accept: (categories, exclusions) => {
        if (pluginInstance) {
            pluginInstance.accept(categories, exclusions);
            return;
        }
        const previous = readAllowedCategories();
        const hadConsent = isConsentValid(readConsentCookie());
        const accepted = resolveAcceptedCategories(categories, exclusions);
        const cookie = writeConsent(accepted);
        dispatchConsentChange({
            categories: cookie.categories,
            changedCategories: hadConsent ? diffCategories(previous, accepted) : []
        });
    },
    allowedCategory: category => readAllowedCategories().includes(category),
    validConsent: () => hasValidConsent(),
    validCookie: cookieName => {
        if (typeof document === 'undefined')
            return false;
        const match = document.cookie.match(`(^|;)\\s*${cookieName}\\s*=\\s*([^;]+)`);
        return Boolean(match?.[2]);
    },
    get: (field, cookieName) => {
        if (cookieName)
            return pluginInstance?.get(field, cookieName) ?? {};
        const cookie = readConsentCookie();
        return cookie?.[field];
    },
    getUserPreferences: () => {
        const accepted = readAllowedCategories();
        return {
            accept_type: accepted.length === CONSENT_CATEGORIES.length
                ? 'all'
                : accepted.length === 1 && accepted.includes(NECESSARY_CATEGORY)
                    ? 'necessary'
                    : 'custom',
            accepted_categories: accepted,
            rejected_categories: CONSENT_CATEGORIES.filter(category => !accepted.includes(category))
        };
    },
    eraseCookies: (cookies, path, domain) => {
        eraseCookies(typeof cookies === 'string' ? [cookies] : cookies, path, domain ? [domain, `.${domain}`] : undefined);
    },
    getConfig: field => {
        const pluginConfig = buildPluginConfig({
            useGoogleAnalytics: pluginSettings.useGoogleAnalytics
        });
        return pluginConfig[field];
    },
    loadScript: (src, callback, attrs) => {
        withPlugin(plugin => {
            plugin.loadScript(src, callback, attrs);
        });
    },
    updateScripts: () => {
        withPlugin(plugin => {
            plugin.updateScripts();
        });
    },
    set: (field, data) => pluginInstance?.set(field, data) ?? false,
    updateLanguage: (lang, force) => pluginInstance?.updateLanguage(lang, force) ?? false
};
const CookieContext = createContext(null);
export const bootstrapCookieConsent = (settings) => {
    if (typeof window === 'undefined')
        return null;
    if (settings?.useGoogleAnalytics !== undefined) {
        pluginSettings.useGoogleAnalytics = settings.useGoogleAnalytics;
    }
    if (settings?.locale !== undefined) {
        pluginSettings.locale = settings.locale;
    }
    window.cookieConsent = consentApi;
    return consentApi;
};
const CONSENT_MODAL_LANGUAGES = buildLanguages();
const CookieConsentBanner = ({ locale }) => {
    const [isVisible, setIsVisible] = useState(true);
    const consentModal = CONSENT_MODAL_LANGUAGES[resolveLanguage(locale, CONSENT_MODAL_LANGUAGES)]
        ?.consent_modal;
    const primaryButton = consentModal?.primary_btn;
    const secondaryButton = consentModal?.secondary_btn;
    const acceptAll = useCallback(() => {
        consentApi.accept(primaryButton?.role === 'accept_all' ? 'all' : []);
    }, [primaryButton?.role]);
    const acceptSecondary = useCallback(() => {
        if (secondaryButton?.role === 'accept_necessary') {
            consentApi.accept([]);
            return;
        }
        consentApi.showSettings(0);
    }, [secondaryButton?.role]);
    const showSettings = useCallback(() => {
        consentApi.showSettings(0);
    }, []);
    useEffect(() => {
        const sync = () => {
            if (hasValidConsent())
                setIsVisible(false);
        };
        sync();
        window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
        return () => {
            window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
        };
    }, []);
    useEffect(() => {
        const onVisibility = (event) => {
            setIsVisible(Boolean(event.detail));
        };
        window.addEventListener(BANNER_VISIBILITY_EVENT, onVisibility);
        return () => {
            window.removeEventListener(BANNER_VISIBILITY_EVENT, onVisibility);
        };
    }, []);
    useEffect(() => {
        const early = window.__JAEN_COOKIE_CONSENT_EARLY_CLICK__;
        window.__JAEN_COOKIE_CONSENT_EARLY_CLICK__ = undefined;
        if (early === 'c-p-bn')
            acceptAll();
        else if (early === 'c-s-bn')
            acceptSecondary();
        else if (early === 'c-settings')
            showSettings();
    }, [acceptAll, acceptSecondary, showSettings]);
    const onDescriptionClick = useCallback((event) => {
        const target = event.target;
        if (!target?.closest?.('[data-cc="c-settings"]'))
            return;
        event.preventDefault();
        showSettings();
    }, [showSettings]);
    if (!isVisible || !consentModal)
        return null;
    return (_jsx("div", { id: "cc--main", [STATIC_BANNER_ATTRIBUTE]: '', style: { position: 'fixed' }, children: _jsxs("div", { className: "cc_div", children: [_jsx("div", { id: "cm", className: "cloud", role: "dialog", "aria-modal": "true", "aria-labelledby": "c-ttl", "aria-describedby": "c-txt", tabIndex: -1, children: _jsxs("div", { id: "c-inr", children: [_jsxs("div", { id: "c-inr-i", children: [_jsx("h2", { id: "c-ttl", children: consentModal.title }), _jsx("div", { id: "c-txt", onClick: onDescriptionClick, dangerouslySetInnerHTML: {
                                            __html: consentModal.description ?? ''
                                        } })] }), _jsxs("div", { id: "c-bns", children: [primaryButton && (_jsx("button", { id: "c-p-bn", className: "c-bn", type: "button", onClick: acceptAll, children: _jsx("span", { tabIndex: -1, children: primaryButton.text }) })), secondaryButton && (_jsx("button", { id: "c-s-bn", className: "c-bn c_link", type: "button", onClick: acceptSecondary, children: _jsx("span", { tabIndex: -1, children: secondaryButton.text }) }))] })] }) }), _jsx("div", { id: "cm-ov" })] }) }));
};
export const CookieConsentProvider = ({ children, useGoogleAnalytics, locale }) => {
    const { colorMode } = useColorMode();
    useEffect(() => {
        bootstrapCookieConsent({ useGoogleAnalytics, locale });
    }, [locale, useGoogleAnalytics]);
    useEffect(() => {
        const isDark = document.documentElement.classList.contains('dark');
        document.documentElement.classList.toggle('c_darkmode', isDark);
    }, [colorMode]);
    return (_jsxs(CookieContext.Provider, { value: consentApi, children: [children, _jsx(CookieConsentBanner, { locale: locale })] }));
};
export default CookieConsentProvider;
export function useCookieConsentContext() {
    const context = useContext(CookieContext);
    return context;
}
export function useCookieConsentCategory(category) {
    const [isAllowed, setIsAllowed] = useState(false);
    useEffect(() => {
        const read = () => {
            setIsAllowed(readAllowedCategories().includes(category));
        };
        read();
        window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, read);
        return () => {
            window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, read);
        };
    }, [category]);
    return isAllowed;
}
